import React, { forwardRef, useMemo, useState } from 'react';

import { widgetComponents } from 'config/widgetConfigurations/widgetComponents';
import { WidgetView } from 'utilitys/reporting/widgetViewModeUtils';

import { useCustomLatestCompareToVersions } from 'hooks/useCustomLatestCompareToVersions';
import useUserHook from 'hooks/useUserHook';

import { useConfigurationStore } from 'store/configurationsStore';

import ErrorBoundary from 'components/common/ErrorBoundary/ErrorBoundary';

import { ExternalComponents } from 'components/Dashboards/Project/Components/CustomDashboard/ExternalComponents/externalComponents';
import { Narrative } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/Narrative/narrative';
import { PinboardOrReportWidgetWrapperProvider } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import { useNarrative } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/hooks/useNarrative';
import { WidgetTopHeader } from 'components/Dashboards/Project/Components/CustomDashboard/WidgetTopHeader/widgetTopHeader';
import { getPermissionsToViewComponent } from 'components/Dashboards/Project/Components/CustomDashboard/customDashboard.utils';
import { NoPermissionWidget } from 'components/Dashboards/Project/Components/CustomDashboard/noPermissionWidget';
import {
    IDashboard,
    ISetSubTitleForExport,
    IWidget,
} from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { WidgetErrorFallback } from 'components/Dashboards/Reporting/widgetInReport/components/widgetErrorFallback/WidgetErrorFallback';

interface Props {
    widget: IWidget;
    projectHierarchyList: IProjectHierarchyList[];
    dashboardId?: number;
    dashboard?: IDashboard;
    setSubTitleForExport: ISetSubTitleForExport;
}

export const PinboardWidgetWrapper = forwardRef(
    (
        { widget, projectHierarchyList, dashboard, setSubTitleForExport }: Props,
        ref?: React.ForwardedRef<HTMLDivElement>,
    ) => {
        const configuration = useConfigurationStore();
        const { ability, email } = useUserHook();
        const [isEdit, setIsEdit] = useState<boolean>(false);
        const handleClickNarrative = () => setIsEdit(!isEdit);

        const {
            id: widgetId,
            projectName,
            contractName,
            projectId,
            contractId,
            componentKey,
            route,
            description,
            payload,
            localFilters,
            showGenerativeNarrative,
        } = widget;

        const componentObj = Boolean(componentKey) ? configuration[componentKey] : null;
        if (!componentObj) return null;

        const { NoPermissionMessage, permission, titleWithoutProjectAndContract } = componentObj;

        const { Component: Widget } = widgetComponents[widget.componentKey];

        const { isHavePermissions } = getPermissionsToViewComponent({
            projectHierarchyList,
            projectId,
            permission: permission !== undefined ? ability.can('view', permission) : undefined,
        });

        const { latestVersionId, compareVersionId } = useCustomLatestCompareToVersions({
            projectId,
            contractId,
            componentKey,
        });

        const bePayload = useMemo(() => {
            return JSON.parse(payload);
        }, [payload]);

        const beLocalFilters = useMemo(() => {
            return JSON.parse(localFilters);
        }, [localFilters]);

        const { narrativeDescription, isGenerativeAi, setIsGenerativeAi, isLoading } = useNarrative({
            email,
            widgetId,
            showGenerativeNarrative,
            description,
        });

        const onCancel = () => {
            //restore generativeAi state to the backend saved state.
            setIsGenerativeAi(showGenerativeNarrative);
            setIsEdit(false);
        };

        if (!Boolean(latestVersionId) && !titleWithoutProjectAndContract) return null;
        return (
            <PinboardOrReportWidgetWrapperProvider
                value={{ isGenerativeAi, setIsGenerativeAi, componentKey, isLoading }}
            >
                <div key={`widget-container-${widgetId}`}>
                    <WidgetTopHeader
                        key={`top-header-${widgetId}`}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        projectName={projectName}
                        contractName={contractName}
                        isHavePermissions={isHavePermissions}
                        titleWithoutProjectAndContract={titleWithoutProjectAndContract}
                        componentKey={componentKey}
                    />
                    {isHavePermissions && Widget ? (
                        <div id={`pinboard-component-${widgetId}`} ref={ref}>
                            <ErrorBoundary fallback={<WidgetErrorFallback />}>
                                <Widget
                                    {...bePayload}
                                    widgetViewMode={WidgetView.pinboard}
                                    widgetId={widgetId}
                                    projectId={projectId}
                                    contractId={contractId}
                                    setSubTitleForExport={setSubTitleForExport}
                                    originalRoute={route}
                                    latestVersionId={latestVersionId}
                                    compareVersionId={compareVersionId}
                                    localFilters={beLocalFilters}
                                    externalComponents={
                                        <ExternalComponents
                                            handleClickNarrative={handleClickNarrative}
                                            originalRoute={route}
                                        />
                                    }
                                    editNarrative={
                                        <Narrative
                                            isEdit={isEdit}
                                            value={narrativeDescription}
                                            handleCancel={onCancel}
                                            handleSave={() => setIsEdit(false)}
                                            widgetId={widgetId}
                                            dashboard={dashboard}
                                        />
                                    }
                                />
                            </ErrorBoundary>
                        </div>
                    ) : (
                        <NoPermissionWidget NoPermissionMessage={NoPermissionMessage} />
                    )}
                </div>
            </PinboardOrReportWidgetWrapperProvider>
        );
    },
);
